import React, { useState } from 'react';
import { MaterialIcon } from '@/components/MaterialIcon';
import OnboardingWelcomeStep from './OnboardingWelcomeStep';
import type AgentApiOrganizationTutorialsIndex from '@/types/serializers/AgentApiOrganizationTutorialsIndex';
import usePostOrganizationTutorials from '@/features/agent/api/usePostOrganizationTutorials';
import useFetchOrganizationTutorials from '@/features/agent/api/useFetchOrganizationTutorials';
import usePostOrganizationTutorialComplete from '@/features/agent/api/usePostOrganizationTutorialComplete';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';

type OnboardingStep = 'welcome' | 'select';

export default function OnboardingFlow({ onProcessingStart }: { onProcessingStart?: () => void }) {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome');
  const { refetch: refetchTutorials } = useFetchOrganizationTutorials();

  const handleNext = () => {
    setCurrentStep('select');
  };

  // selectステップ完了後は、DashboardTemplate内のOnboardingコンポーネントが表示される
  const handleSelectComplete = async () => {
    // チュートリアルを再度取得してDashboardTemplateの状態を更新
    await refetchTutorials();
  };

  switch (currentStep) {
    case 'welcome':
      return <OnboardingWelcomeStep onNext={handleNext} />;
    case 'select':
      return (
        <OnboardingSelectStepModified
          onComplete={handleSelectComplete}
          refetchTutorials={refetchTutorials}
          onProcessingStart={onProcessingStart}
        />
      );
  }
}

// OnboardingSelectStep（ダッシュボード統合版）
function OnboardingSelectStepModified({
  onComplete,
  refetchTutorials,
  onProcessingStart,
}: {
  onComplete: () => void;
  refetchTutorials: () => Promise<{ data?: AgentApiOrganizationTutorialsIndex }>;
  onProcessingStart?: () => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const { mutateAsync: postOrganizationTutorials } = usePostOrganizationTutorials();
  const { mutateAsync: postOrganizationTutorialComplete } = usePostOrganizationTutorialComplete();

  const options = [
    {
      key: 'data_management',
      title: 'データの管理をしたい',
      description: '求人、求職者、企業などの\nデータを一元管理可能です。',
      icon: '/agent/onboarding/management.png',
    },
    {
      key: 'suggest_job_post',
      title: '求職者に求人を提案したい',
      description: '求職者の希望に合う求人を\n簡単に提案することができます。',
      icon: '/agent/onboarding/suggestion.png',
    },
    {
      key: 'entry_from',
      title: 'エントリーフォームを作りたい',
      description: '求職者の情報を集約できる\nエントリーフォームを作成できます。',
      icon: '/agent/onboarding/making-entryform.png',
    },
  ];

  const handleToggle = (key: string) => {
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  // スキップ処理：デフォルトチュートリアルを作成・完了して直接ダッシュボードへ
  const onSkip = async () => {
    if (onProcessingStart) {
      onProcessingStart();
    }

    try {
      // 1. デフォルトチュートリアル作成
      await postOrganizationTutorials(['data_management']);

      // 2. 作成されたチュートリアルを取得
      const refetchResult = await refetchTutorials();

      // 3. チュートリアルを完了状態にする
      const latestTutorialId = refetchResult.data?.records[0]?.id;
      if (latestTutorialId) {
        await postOrganizationTutorialComplete(latestTutorialId);

        // 4. 最終的な状態を更新（処理中フラグでダッシュボード表示を制御）
        await refetchTutorials();
      } else {
        showErrorToast('チュートリアルIDが取得できませんでした');
      }
    } catch (error) {
      showErrorToast('スキップに失敗しました');
    }
  };

  // 次へ処理：選択されたチュートリアルを作成してsetupステップへ
  const onHome = async () => {
    try {
      // チュートリアル作成API実行
      await postOrganizationTutorials(selected);

      // setupステップに進む（DashboardTemplate内のOnboardingコンポーネントが表示される）
      onComplete();
    } catch (error) {
      showErrorToast('チュートリアル作成に失敗しました');
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-gray-100"
      style={{ backgroundColor: '#F4F6F9' }}
    >
      {/* Overlay & 背景装飾 */}
      <div
        className="absolute inset-0 bg-gray-300 opacity-50"
        style={{ backgroundColor: '#E7E7E7' }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute rounded-full"
            style={{
              background: '#EEE6F6',
              width: '796.91px',
              height: '630.18px',
              right: '-100px',
              top: '-100px',
              filter: 'blur(200px)',
            }}
          ></div>
          <div
            className="absolute rounded-full"
            style={{
              background: '#C6E2F4',
              width: '796.91px',
              height: '630.18px',
              left: '-100px',
              bottom: '-100px',
              filter: 'blur(200px)',
            }}
          ></div>
        </div>
      </div>

      {/* Main Dialog */}
      <div
        className="relative z-10 mx-4 w-full overflow-hidden rounded-md bg-white shadow-xl"
        style={{ maxWidth: '640px', boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.2)' }}
      >
        <div className="flex flex-col items-center gap-8 px-16 py-8">
          <h2 className="my-0 text-center text-2xl font-bold text-black">
            使いたい機能を選んでください
          </h2>

          {/* 選択肢リスト */}
          <div className="flex w-full flex-col gap-3">
            {options.map((opt) => (
              <div
                key={opt.key}
                className={`flex w-full cursor-pointer items-center gap-6 rounded-sm border border-solid px-6 py-4 select-none ${
                  selected.includes(opt.key)
                    ? 'border-primary bg-[#EDF8FF]'
                    : 'border-border bg-white'
                }`}
                style={{ minHeight: 107 }}
                onClick={() => handleToggle(opt.key)}
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.includes(opt.key)}
                    onChange={() => handleToggle(opt.key)}
                    className="size-4"
                  />
                </div>
                <div className="flex-1">
                  <div
                    className={`mb-[10px] text-lg font-bold text-black ${
                      selected.includes(opt.key) ? 'text-primary' : ''
                    }`}
                  >
                    {opt.title}
                  </div>
                  <div className="text-base font-normal whitespace-pre-line text-[#262626]">
                    {opt.description}
                  </div>
                </div>
                <img
                  src={opt.icon}
                  alt="icon"
                  className="h-[107px] w-[132px] rounded object-contain"
                />
              </div>
            ))}
          </div>

          {/* ボタン */}
          <div className="mt-8 flex w-full flex-col items-center gap-2">
            <button
              type="button"
              onClick={onHome}
              disabled={selected.length === 0}
              className="relative flex h-[52px] w-[240px] items-center justify-center rounded bg-primary font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              次へ
              <MaterialIcon name="keyboard_arrow_right" className="absolute right-md text-lg" />
            </button>
            <button
              type="button"
              onClick={onSkip}
              className="h-[52px] w-[240px] rounded border border-gray-300 bg-white font-bold text-gray-700"
            >
              スキップ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
